import { BASE_URL, I18N_LOCALE } from "constants/enum";

// Full i18n.locales list (incl. `default`), mirroring Next's analyze().
const LOCALE_PREFIXES = new Set(I18N_LOCALE);

/** Normalizes a path: strips query/hash and trailing slash (except root). */
export const normalizePath = (path: string): string => {
    const without = path.split("?")[0].split("#")[0];
    if (without.length > 1 && without.endsWith("/")) {
        return without.slice(0, -1);
    }
    return without || "/";
};

/** Extracts pathname from an absolute or site-relative href. */
export const pathnameFromHref = (href: string): string => {
    if (href.startsWith("http")) {
        try {
            return new URL(href).pathname;
        } catch {
            return href;
        }
    }
    try {
        return new URL(href, BASE_URL).pathname;
    } catch {
        return href.startsWith("/") ? href : `/${href}`;
    }
};

/**
 * Removes Next.js i18n locale prefix from a path (`/es/foo` → `/foo`).
 * When locale is missing or `"default"`, returns the path unchanged.
 */
export const stripLocalePrefix = (
    pathname: string,
    locale: string | undefined
): string => {
    const pathOnly = normalizePath(pathname);
    if (!locale || locale === "default") {
        return pathOnly;
    }
    const prefix = `/${locale}`;
    if (pathOnly === prefix) {
        return "/";
    }
    if (pathOnly.startsWith(`${prefix}/`)) {
        return normalizePath(pathOnly.slice(prefix.length) || "/");
    }
    return pathOnly;
};

/**
 * Strips any leading locale segments (`/uk/es/foo` → `/foo`), preventing
 * double-locale URLs that trip Next's i18n invariant. Unlike `stripLocalePrefix`
 * it removes any locale, not just a known one.
 */
export const removeLeadingLocalePrefixes = (pathname: string): string => {
    const segments = pathname.split("/");
    let index = 1;
    // Lowercase before matching: Next detects locales case-insensitively.
    while (
        index < segments.length &&
        LOCALE_PREFIXES.has(segments[index].toLowerCase())
    ) {
        index += 1;
    }

    if (index === 1) {
        return pathname;
    }

    const rest = segments.slice(index).join("/");
    return rest ? `/${rest}` : "/";
};
